import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Plus, Car, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import JobCardStatusGrid from './components/JobCardStatusGrid';
import TodayActivityStrip from './components/TodayActivityStrip';
import PendingActionsPanel from './components/PendingActionsPanel';
import PriorityJobsPanel from './components/PriorityJobsPanel';
import RecentJobCards from './components/RecentJobCards';

const DashboardPage: React.FC = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    document.title = 'Workshop Dashboard - Service Centre';
    return () => {
      document.title = 'Billing App';
    };
  }, []);

  const handleRefresh = () => {
    // Invalidate all workshop dashboard queries
    queryClient.invalidateQueries({ queryKey: ['workshopDashboard'] });
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            Workshop Dashboard
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
            {format(new Date(), 'EEEE, MMMM d, yyyy')}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button asChild variant="default" size="sm" className="gap-2">
            <Link to="/workshop/job-cards/detail">
              <Plus className="h-4 w-4" />
              New Job Card
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm" className="gap-2">
            <Link to="/workshop/vehicles/detail">
              <Car className="h-4 w-4" />
              New Vehicle
            </Link>
          </Button>
          <Button
            onClick={handleRefresh}
            variant="ghost"
            size="icon"
            title="Refresh Dashboard"
            className="h-8 w-8"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Job Card Status Grid - Hero Section */}
      <section>
        <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wide">
          Job Card Status
        </h2>
        <JobCardStatusGrid />
      </section>

      {/* Today's Activity Strip */}
      <section>
        <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wide">
          Today's Activity
        </h2>
        <TodayActivityStrip />
      </section>

      {/* Attention Required - Split Panel */}
      <section>
        <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wide">
          Attention Required
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PendingActionsPanel />
          <PriorityJobsPanel />
        </div>
      </section>

      {/* Recent Job Cards */}
      <section>
        <RecentJobCards />
      </section>
    </div>
  );
};

export default DashboardPage;
